import $ from 'jquery'
import _ from 'lodash'
import appDisplay from 'scoreboard/components/app-display'
import HttpStatusCodes from 'http-status-codes'
import constants from 'scoreboard/components/constants'
import Nav from 'scoreboard/components/nav'
import EditTreeDialog from 'scoreboard/components/edit-tree-dialog'
import permissions from 'scoreboard/components/permissions'
import Tooltip from 'scoreboard/components/tooltip'
import i18n from 'scoreboard/components/i18n'
import { isV5 } from 'scoreboard/components/version'

// The project-organization-nav brings together common functionality used by both
// the project-nav and the organizations-nav
var ProjectOrganizationNav = Nav.extend({
	// when switching to edit mode, don't fiddle with the url
	changeUrl: false,

	_bindEvents: function() {
		Nav.prototype._bindEvents.apply(this, arguments)
		this.listenTo(this.gardener, 'success', () => {
			let tree = this.gardener.tree
			tree.on('dndstart', evt => this._removeEditTooltip())
		})
	},

	// handles a change to the selection in the tree when the tree is editable
	selectEditable: function(node) {
		// If we're already showing the edit tooltip for the newly selected node
		if (this.editTooltip && !this.editTooltip.removed &&
				this.editTooltip.attachedTo().is(this.gardener.tree.selectedEl())) {
			// don't close the tooltip
			window.event.stopPropagation()
			return
		}

		this._removeEditTooltip()
		// if we were searching on something
		if (this.finderText) {
			this.gardener.tree.once('selected', function() {
				this._createEditTooltip(node)
			}.bind(this))
		} else {
			this._createEditTooltip(node)
		}
	},

	// Show or hide the nav edit tooltip based on the selected node and whether it's showing or not.
	// Used in organizations-nav and projects-nav to show or hide their pop-out edit menus.
	showNavEditTooltip: function(edit) {
		var selected = this.gardener.tree.selected()
		// if a user clicks on the edit button, let's go ahead and show them
		// the tooltip popup
		if (edit && selected && this._editWithTooltips()) {
			this._removeEditTooltip()
			this._createEditTooltip(selected)
		} else {
			this._removeEditTooltip()
		}
	},

	/**
	 * Toggles the tree actions and edit tooltip as appropriate.
	 * When in edit mode, hides the tree actions and displays the edit tooltip.
	 * When not in edit mode, displays the tree actions.
	 */
	toggleEdit: function() {
		Nav.prototype.toggleEdit.apply(this, arguments)
		if (this._edit) {
			this.treeActions?.hideActions()
			this.showNavEditTooltip(true)
		} else {
			this._removeEditTooltip()
			this.treeActions?.showActions()
		}
	},


	toggleTree: function(show) {
		Nav.prototype.toggleTree.apply(this, arguments)
		if (show && this.$('.tree-container').length > 0) {
			this.$('.tree-container').addClass('tree-container-dark')
		}
	},

	/**
	 * Selects the current project/organization in the gardener
	 */
	selectCurrent() {},

	show: function(opt) {
		return new Promise(result => {
			//#22703 when reusing the nav and showing a new tree, undelegate the events on the tree actions while the
			//new tree is setup so it doesn't fire any events that are associated with the old tree (the new tree won't be
			//associated with treeActions until after the tree is loaded)
			if (this.treeActions) {
				this.treeActions.undelegateEvents()
			}

			_.invoke(this.gardener, 'show', _.defaults({}, opt, { silent: true }))
			_.extend(this.options, opt)

			this.gardener._onceTreeRendered(() => {
				// We're now showing the nav. Make sure the current project/org is selected.
				this.selectCurrent()
				//#29111 bind the tree actions to the new tree (and don't wait for it
				//to be fully rendered) if it was reloaded by the gardener
				this.renderTreeActions()
				this.treeActions.delegateEvents()
				result(this)
			})
		})
	},

	/**
	 * @inheritdoc
	 */
	onSelect: function(node) {
		this.gardener.finder.clear()
		if (this.gardener.editable() && this._editWithTooltips()) {
			this.selectEditable(node)
		}
	},

	// NOTE: For the org/project panes, we're hiding the 'Select' button when the app's
	// width is > constants.NAV_EDIT_MENU_CUTOFF. tree-actions.js has no knowlege of this
	// so this function is fired whether or not that button is visible.
	_conditionallyShowTooltip: function(node) {
		if (!node) {
			return
		}

		// Pull up the mobile edit dialog if the screen is narrow enough
		if (!this._editWithTooltips()) {
			var dialog = EditTreeDialog.extend({
				gardener: this.gardener,
				EditTreeMenu: this.EditTreeMenu
			})
			appDisplay.dialog(new dialog({
				model: new this.Model(node)
			}))
		}
	},

	gardenerError: function(stream) {
		if (stream.statusCode == HttpStatusCodes.NOT_FOUND) {
			// display the create button
			this.toggleTree(false)
		} else {
			this.$el.empty()
		}
	},

	getSelected: function() {
		return _.invoke(this.gardener.tree, 'selected')
	},

	_editTooltipOptions: function(selectedNode) {
		return {
			closeOnDialogOpen: false,
			attachTo: selectedNode,
			placement: 'right',
			arrow: 'top',
			closeOnAttachedToClick: false
		}
	},

	_createEditTooltip: function(node, renderTooltip) {
		if (!permissions.modifyProjects() && !node.modify) {
			return
		}

		var selectedNode = $(this.gardener.tree.selectedEl())
		// guard against possible race condition.  if the user searched and then
		// rapidly clicked around, it's possible the place where we originally
		// planned to put the tooltip isn't there anymore
		if (!selectedNode.length) {
			return
		}

		// #56330 if the navigation isn't visible, don't try to attach a tooltip
		// to it!
		if (isV5() && !this.$el.hasClass('is-visible')) {
			return
		}

		let content
		if (node.pendingDeletion) {
			content = i18n.get('common.organization.aboutToBeDeleted')
			let options = _.extend({ content }, this._editTooltipOptions(selectedNode))
			this.editTooltip = new Tooltip(options).render()
		} else {
			this.tooltipContent = new this.EditTreeMenu({
				model: new this.Model(node),
				gardener: this.gardener
			}).render()
			this.tooltipContent.on('orgMoved', () => this._removeEditTooltip())
			// we don't want the tooltip to render again so pass in the el
			content = this.tooltipContent.el

			if (this._userHasEditPermission ? this._userHasEditPermission() : true) {
				let options = _.extend({
					content,
					extraClass: 'tree-tooltip is-under-dialog'
				}, this._editTooltipOptions(selectedNode))
				this.editTooltip = new Tooltip(options).render()
			}
		}
	},

	_removeEditTooltip: function () {
		this.editTooltip && this.editTooltip.remove()
		delete this.editTooltip
	},

	_editWithTooltips: function() {
		return $('body').width() > constants.NAV_EDIT_MENU_CUTOFF
	},

	remove: function() {
		ProjectOrganizationNav.prototype.remove.apply(this, arguments)
		this._removeEditTooltip()
		_.invokeMap([this.treeActions], 'remove')
	}
})

export default ProjectOrganizationNav

ProjectOrganizationNav.__filename__ = 'components/project-organization-nav.js'
